import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ROUTES } from '../../utils/constants';
import { runLmsLaunchFlow } from '../../integrations/banyanpro/launch/launchFlow';
import { stripAssertionFromUrl } from '../../integrations/banyanpro/security/stripAssertionFromUrl';
import { extractAssertionJwt, getDefaultRouteForUser } from '../../utils/lmsLaunch';
import { getBanyanproEmbedEnabled, isEmbedPostMessageMode } from '../../integrations/banyanpro/config/embedConfig';
import { startPostMessageLaunch } from '../../integrations/banyanpro/launch/LaunchController';
import { getEmbedErrorPresentation, LMS_EMBED_DISABLED } from '../../integrations/banyanpro/errors/embedErrors';
import type { LmsLaunchErrorPresentation } from '../../utils/lmsLaunchErrors';

type Phase = 'waiting' | 'launching' | 'error';

/**
 * Production LMS authenticate page (/authenticate).
 *
 * Primary modes (JWT never in URL):
 *   1. postMessage from parent iframe (BanyanPro sends BANYANPRO_ASSERTION)
 *   2. Query-param fallback for backward compatibility (strips JWT immediately)
 */
export const AuthenticatePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeLmsSession, user, isAuthenticated } = useAuth();
  const [phase, setPhase] = useState<Phase>('waiting');
  const [errorPresentation, setErrorPresentation] = useState<LmsLaunchErrorPresentation>(
    getEmbedErrorPresentation(LMS_EMBED_DISABLED)
  );
  const startedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | undefined>();

  const returnUrlParam = searchParams.get('returnUrl') ?? searchParams.get('return_url');

  const runQueryFallback = useCallback(async (): Promise<boolean> => {
    // Capture token before modifying URL
    const assertion = extractAssertionJwt(searchParams);
    const token = assertion;
    if (process.env.NODE_ENV === 'development') {
      console.debug('[LMS Authenticate] token found', Boolean(token), 'length', token?.length);
    }
    if (!token) {
      return false;
    }
    stripAssertionFromUrl();
    setPhase('launching');
    const result = await runLmsLaunchFlow(token, {
      returnUrl: returnUrlParam,
      completeLmsSession,
    });
    if (result.ok) {
      navigate(result.targetPath, { replace: true });
      return true;
    }
    setErrorPresentation(result.presentation);
    setPhase('error');
    return true;
  }, [searchParams, returnUrlParam, completeLmsSession, navigate]);

  const startPostMessage = useCallback(() => {
    cleanupRef.current?.();
    setPhase('waiting');

    if (!getBanyanproEmbedEnabled()) {
      setErrorPresentation(getEmbedErrorPresentation(LMS_EMBED_DISABLED));
      setPhase('error');
      return;
    }

    cleanupRef.current = startPostMessageLaunch(
      { completeLmsSession, navigate, returnUrl: returnUrlParam },
      {
        onWaiting: () => setPhase('waiting'),
        onLaunching: () => setPhase('launching'),
        onError: (p) => {
          setErrorPresentation(p);
          setPhase('error');
        },
        onSuccess: () => { /* navigate handled in controller */ },
      }
    );
  }, [completeLmsSession, navigate, returnUrlParam]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      if (await runQueryFallback()) return;

      if (isEmbedPostMessageMode()) {
        startPostMessage();
        return;
      }

      setErrorPresentation({
        title: 'Missing launch token',
        message: 'No sign-in token was provided. Open Tamil Katral Maiyam from your learning portal (BanyanPro) to continue.',
        canRetry: false,
      });
      setPhase('error');
    })();

    return () => {
      cleanupRef.current?.();
    };
  }, [runQueryFallback, startPostMessage]);

  const handleRetry = () => {
    startedRef.current = true;
    startPostMessage();
  };

  if (phase === 'waiting') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4">
        <Spinner size="lg" />
        <p className="text-center text-slate-600">Waiting for your learning portal…</p>
      </div>
    );
  }

  if (phase === 'launching') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4">
        <Spinner size="lg" />
        <p className="text-center text-slate-600">Signing you in from your learning portal…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-slate-900">{errorPresentation.title}</h1>
        <p className="mt-2 text-sm text-slate-600">{errorPresentation.message}</p>
        {errorPresentation.supportHint && (
          <p className="mt-3 text-xs text-slate-500">{errorPresentation.supportHint}</p>
        )}
        {errorPresentation.code && (
          <p className="mt-2 font-mono text-xs text-slate-400">Code: {errorPresentation.code}</p>
        )}
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          {errorPresentation.canRetry && (
            <Button type="button" onClick={handleRetry}>
              Try again
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
          >
            Go to sign in
          </Button>
        </div>
        {isAuthenticated && user && (
          <p className="mt-4 text-xs text-slate-500">
            You may already be signed in.{' '}
            <button
              type="button"
              className="text-indigo-600 underline"
              onClick={() => navigate(getDefaultRouteForUser(user), { replace: true })}
            >
              Continue to your dashboard
            </button>
          </p>
        )}
      </div>
    </div>
  );
};
